import { useMemo } from 'react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useMasterNotifications } from '@/hooks/master/useMasterNotifications'

type MasterPanelProps = {
  enabled?: boolean
}

export function MasterNotificationsPanel({ enabled = true }: MasterPanelProps) {
  const { timeline, hitl, events, loading, refresh, available } = useMasterNotifications({ enabled })
  const combined = useMemo(() => [...timeline, ...hitl, ...events].sort((a, b) => {
    const ta = new Date(a.timestamp ?? Date.now()).getTime()
    const tb = new Date(b.timestamp ?? Date.now()).getTime()
    return tb - ta
  }), [timeline, hitl, events])
  const isUnavailable = enabled && !available

  return (
    <Card className="border border-border/70 bg-background/70">
      <CardHeader className="flex items-center justify-between gap-2">
        <CardTitle className="text-lg">Unified notifications</CardTitle>
        <Badge variant="outline" className="text-xs">
          {combined.length}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {isUnavailable ? (
          <p className="text-xs text-muted-foreground">Master insights are unavailable in this environment.</p>
        ) : loading && combined.length === 0 ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : combined.length === 0 ? (
          <p className="text-xs text-muted-foreground">No notifications yet.</p>
        ) : (
          <ul className="space-y-2">
            {combined.slice(0, 6).map((item) => (
              <li key={item.id ?? item.timestamp ?? Math.random()} className="rounded-xl border border-border/80 bg-background/40 p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-foreground">{item.summary ?? item.type}</span>
                  <Badge variant="outline" className="text-xs">
                    {new Date(item.timestamp ?? Date.now()).toLocaleTimeString()}
                  </Badge>
                </div>
                {item.payload && (
                  <p className="text-xs text-muted-foreground">
                    {JSON.stringify(item.payload).slice(0, 80)}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
        <button
          type="button"
          className="text-xs uppercase tracking-wide text-primary-foreground dark:text-amber-200 hover:underline disabled:opacity-50"
          onClick={() => refresh?.()}
          disabled={!available}
        >
          Refresh
        </button>
      </CardContent>
    </Card>
  )
}
